import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Plus } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { MaskedLines } from "@/components/Motion";
import { EventCard } from "@/components/EventCard";
import EventDialog from "@/components/EventDialog";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";

export default function Events() {
  useSeo("Sponsorship Events and Opportunities", "Browse upcoming conferences, festivals, fashion weeks, sports fixtures and brand activations looking for sponsors and partners.", "/events");
  const { user } = useAuth();
  const nav = useNavigate();
  const [type, setType] = useState("");
  const [when, setWhen] = useState("upcoming");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/events", { params: { type, when, q } })
        .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
        .catch(() => setItems([]));
    }, 200);
    return () => clearTimeout(t);
  }, [type, when, q]);

  const chip = (active) => `rounded-full px-4 py-2 text-sm font-semibold transition-colors ${active ? "bg-[#2A1240] text-white" : "border border-[#2A1240]/15 text-slate-200 hover:border-[#E0218A]"}`;

  return (
    <div className="min-h-screen bg-[#FFF7FB]" data-testid="events-page">
      <Nav />
      <section className="pt-32 pb-10 mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="kicker">Events</p>
            <MaskedLines as="h1" className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-[#2A1240] tracking-tight"
              lines={["What’s happening", <em key="e" className="italic gold-text">across the industry.</em>]} />
          </div>
          <button className="btn-gold self-start" onClick={() => nav(user ? "/app/events?new=1" : "/login?next=/app/events?new=1")} data-testid="events-post-button">
            <Plus className="h-4 w-4" /> Post an event
          </button>
        </div>
        <div className="mt-10 flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#E0218A]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, host or city" className="field !pl-11 !rounded-full" data-testid="events-search-input" />
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {[["", "All"], ["marketing", "Marketing"], ["partnership", "Partnership"]].map(([v, l]) => (
              <button key={l} onClick={() => setType(v)} className={chip(type === v)} data-testid={`events-type-${l.toLowerCase()}`}>{l}</button>
            ))}
            <span className="w-px bg-[#2A1240]/10 mx-1" />
            {[["upcoming", "Upcoming"], ["past", "Past"], ["", "Any time"]].map(([v, l]) => (
              <button key={l} onClick={() => setWhen(v)} className={`${chip(when === v)} shrink-0`} data-testid={`events-when-${l.toLowerCase().replace(" ", "-")}`}>{l}</button>
            ))}
          </div>
        </div>
      </section>
      <section className="pb-24 mx-auto max-w-7xl px-5 sm:px-8">
        {!items.length && (
          <div className="rounded-2xl border border-dashed border-[#2A1240]/20 bg-white p-10 text-center" data-testid="events-empty">
            <p className="font-semibold text-[#2A1240]">No events here yet. Be the first to post one.</p>
            <p className="mt-2 text-sm text-slate-300">Post your conference, festival, launch or activation and reach sponsors and partners in every country.</p>
            <Link to="/app/events?new=1" className="btn-gold mt-5" data-testid="events-empty-cta">Post an event</Link>
          </div>
        )}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="events-grid">
          {items.map((e, i) => <EventCard key={e.id} e={e} i={i} onOpen={setOpen} />)}
        </div>
      </section>
      <EventDialog event={open} onClose={() => setOpen(null)} />
      <Footer />
    </div>
  );
}
